import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { Link, useSearch } from "@tanstack/react-router";
import {
  BanknoteIcon,
  DownloadIcon,
  ExternalLinkIcon,
  FileTextIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { usePerformanceRevenueReportQuery } from "../api/queries";
import {
  PerformanceReportDateFilter,
  type PerformanceReportSearch,
  usePerformanceReportDates,
} from "../ui/PerformanceReportDateFilter";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
} from "@/modules/reports/presentation/ui/ReportSummaryStatCard";
import {
  escapeCsvCell,
  formatCurrencyBuckets,
  formatPerformanceDateTime,
  formatPerformanceMoney,
  PerformanceReportsNav,
  summaryStatCurrencyBucketOptions,
} from "../ui/PerformanceReportsNav";

const DOCUMENT_TYPE_LABELS = {
  quotation: "ໃບສະເໜີລາຄາ",
  invoice: "ໃບເກັບເງິນ",
  receipt: "ໃບຮັບເງິນ",
} as const;

export function PerformanceRevenueReportPage() {
  const search = useSearch({
    from: "/app/performance/reports/revenue",
  }) as PerformanceReportSearch;

  const { dateFrom, dateTo } = usePerformanceReportDates(search);
  const offset = search.offset ?? 0;
  const limit = 20;

  const query = useMemo(
    () => ({
      dateFrom,
      dateTo,
      documentType: search.documentType as
        | "quotation"
        | "invoice"
        | "receipt"
        | undefined,
      offset,
      limit,
    }),
    [dateFrom, dateTo, search.documentType, offset],
  );

  const { data, isLoading } = usePerformanceRevenueReportQuery(query);
  const rows = data?.data ?? [];
  const summary = data?.summary;
  const total = data?.total ?? 0;

  const exportCsv = useCallback(() => {
    const header = "ເລກທີ,ປະເພດ,ສະກຸນເງິນ,ງານ,ວັນທີອອກ,ຍອດ";
    const csvRows = rows.map((row) =>
      [
        escapeCsvCell(row.documentNo),
        escapeCsvCell(row.documentType),
        escapeCsvCell(row.currency),
        escapeCsvCell(row.eventTitle),
        escapeCsvCell(
          row.issuedAt ? formatPerformanceDateTime(row.issuedAt) : "",
        ),
        escapeCsvCell(row.grandTotal),
      ].join(","),
    );
    const csv = [header, ...csvRows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `performance-revenue-report-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, dateFrom, dateTo]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ງານສະແດງ", to: "/app/performance/events" },
            { label: "ລາຍງານງານສະແດງ", to: "/app/performance/reports" },
            { label: "ລາຍຮັບ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ລາຍງານລາຍຮັບງານສະແດງ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ເອກະສານລູກຄ້າແຍກຕາມປະເພດ QT / INV / RCPT
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={rows.length === 0}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫລດ CSV
          </Button>
        </div>

        <PerformanceReportsNav />

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <PerformanceReportDateFilter
            routeFrom="/app/performance/reports/revenue"
            showDocumentType
          />
        </div>

        <ReportSummaryStatGrid columns={4}>
          <ReportSummaryStatCard
            title="ໃບສະເໜີລາຄາ"
            icon={FileTextIcon}
            tone="attendance"
            isLoading={isLoading}
            value={formatCurrencyBuckets(summary?.quoted, summaryStatCurrencyBucketOptions)}
          />
          <ReportSummaryStatCard
            title="ໃບເກັບເງິນ"
            icon={FileTextIcon}
            tone="packages"
            isLoading={isLoading}
            value={formatCurrencyBuckets(summary?.invoiced, summaryStatCurrencyBucketOptions)}
          />
          <ReportSummaryStatCard
            title="ເກັບແລ້ວ"
            icon={BanknoteIcon}
            tone="revenue"
            isLoading={isLoading}
            value={formatCurrencyBuckets(summary?.collected, summaryStatCurrencyBucketOptions)}
          />
          <ReportSummaryStatCard
            title="ຄ້າງເກັບ"
            icon={BanknoteIcon}
            tone="refunds"
            isLoading={isLoading}
            value={formatCurrencyBuckets(
              summary?.outstandingAr,
              summaryStatCurrencyBucketOptions,
            )}
          />
        </ReportSummaryStatGrid>

        <Card>
          <CardHeader>
            <CardTitle>ຕາຕະລາງເອກະສານ ({total})</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {isLoading ? (
              <p className="text-muted-foreground text-sm">ກຳລັງໂຫລດ...</p>
            ) : rows.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                ບໍ່ມີຂໍ້ມູນໃນຊ່ວງວັນທີທີ່ເລືອກ
              </p>
            ) : (
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pr-4 pb-2 font-medium">ເລກທີ</th>
                    <th className="pr-4 pb-2 font-medium">ປະເພດ</th>
                    <th className="pr-4 pb-2 font-medium">ສະກຸນ</th>
                    <th className="pr-4 pb-2 font-medium">ງານ</th>
                    <th className="pr-4 pb-2 font-medium">ວັນທີອອກ</th>
                    <th className="pr-4 pb-2 text-right font-medium">ຍອດ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b last:border-0">
                      <td className="py-3 pr-4 font-medium">
                        {row.documentNo}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant="outline">
                          {DOCUMENT_TYPE_LABELS[
                            row.documentType as keyof typeof DOCUMENT_TYPE_LABELS
                          ] ?? row.documentType}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant="secondary">{row.currency}</Badge>
                      </td>
                      <td className="py-3 pr-4">
                        <Link
                          to="/app/performance/events/$id"
                          params={{ id: row.eventId }}
                          className="inline-flex items-center gap-1 hover:underline"
                        >
                          {row.eventTitle}
                          <ExternalLinkIcon className="size-3" />
                        </Link>
                      </td>
                      <td className="whitespace-nowrap py-3 pr-4">
                        {formatPerformanceDateTime(row.issuedAt)}
                      </td>
                      <td className="whitespace-nowrap py-3 pr-4 text-right">
                        {formatPerformanceMoney(row.grandTotal, row.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
